import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, CheckCircle2, Phone, Mail, Instagram, MapPin } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { BRAND, SERVICES } from "@/data";
import { Reveal, Eyebrow } from "./Reveal";

const EMPTY = {
  name: "", email: "", phone: "", organization: "",
  service: SERVICES[0].title, pickup: "", destination: "", date: "", message: "",
};

const field = "w-full bg-ink border border-gold/20 px-4 py-3 text-cream placeholder:text-cream/30 focus:outline-none focus:ring-2 focus:ring-gold/60 transition";

export default function QuoteSection() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/quotes", form);
      setDone(true);
      setForm(EMPTY);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send. Please call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="relative py-28 lg:py-40 bg-ink" data-testid="quote">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
        <div>
          <Reveal><Eyebrow>Request a Quote</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif font-light text-cream text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Tell us where <span className="italic text-gold">it needs to go.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 text-cream/60 font-light leading-relaxed max-w-md">
              Share a few details and our team responds fast — usually the same day. Prefer to talk?
              Call us directly, we're always happy to help.
            </p>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="mt-10 space-y-5">
              <ContactRow icon={Phone} label={BRAND.phone} href={BRAND.phoneHref} testid="contact-phone" />
              <ContactRow icon={Mail} label={BRAND.email} href={`mailto:${BRAND.email}`} testid="contact-email" />
              <ContactRow icon={Instagram} label={`@${BRAND.instagram}`} href={BRAND.instagramUrl} testid="contact-ig" />
              <ContactRow icon={MapPin} label={BRAND.address} testid="contact-address" />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="bg-ink-2 border border-gold/15 p-8 lg:p-10">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="done"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  data-testid="quote-success"
                  className="text-center py-16"
                >
                  <CheckCircle2 size={56} className="text-gold mx-auto" strokeWidth={1} />
                  <h3 className="mt-6 font-serif text-3xl text-cream">Request received.</h3>
                  <p className="mt-3 text-cream/60 font-light">
                    Thank you — we'll be in touch shortly at the details you provided.
                  </p>
                  <button
                    onClick={() => setDone(false)}
                    className="mt-8 border border-gold/50 text-gold px-6 py-3 text-sm tracking-widest btn-gold"
                  >
                    SEND ANOTHER
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  onSubmit={submit}
                  className="space-y-4"
                >
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input required placeholder="Full name" value={form.name} onChange={set("name")} className={field} data-testid="quote-name" />
                    <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={field} data-testid="quote-email" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input placeholder="Phone" value={form.phone} onChange={set("phone")} className={field} data-testid="quote-phone" />
                    <input placeholder="Organization / NGO" value={form.organization} onChange={set("organization")} className={field} data-testid="quote-org" />
                  </div>
                  <select value={form.service} onChange={set("service")} className={field} data-testid="quote-service">
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.title} className="bg-ink">{s.title}</option>
                    ))}
                  </select>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input placeholder="Pickup location" value={form.pickup} onChange={set("pickup")} className={field} data-testid="quote-pickup" />
                    <input placeholder="Destination" value={form.destination} onChange={set("destination")} className={field} data-testid="quote-destination" />
                  </div>
                  <input type="text" onFocus={(e)=>(e.target.type='date')} placeholder="Preferred date" value={form.date} onChange={set("date")} className={field} data-testid="quote-date" />
                  <textarea rows={4} placeholder="Tell us more about your needs" value={form.message} onChange={set("message")} className={field} data-testid="quote-message" />

                  {error && <p className="text-sm text-red-400" data-testid="quote-error">{error}</p>}

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="quote-submit"
                    className="w-full btn-gold border border-gold text-gold py-4 tracking-widest text-sm flex items-center justify-center gap-2"
                  >
                    {loading && <Loader2 size={18} className="animate-spin" />}
                    {loading ? "SENDING..." : "SEND REQUEST"}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function ContactRow({ icon: Icon, label, href, testid }) {
  const inner = (
    <span className="flex items-center gap-4 text-cream/80 hover:text-gold transition-colors group">
      <span className="h-11 w-11 flex items-center justify-center border border-gold/25 group-hover:border-gold/60 transition-colors">
        <Icon size={18} className="text-gold" />
      </span>
      <span className="tracking-wide">{label}</span>
    </span>
  );
  return href ? (
    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={testid}>{inner}</a>
  ) : (
    <div data-testid={testid}>{inner}</div>
  );
}
